import { useEffect, useRef } from "react";
import { ArrowRight, Truck, RotateCcw } from "lucide-react";

export default function Hero() {
  const shoeRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia("(max-width: 768px)").matches) return;
    const el = shoeRef.current;
    if (!el) return;
    let raf = 0;
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const { innerWidth: w, innerHeight: h } = window;
        const x = (e.clientX / w - 0.5) * 24;
        const y = (e.clientY / h - 0.5) * 18;
        el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  const marqueeWords = [
    "LIGHTWEIGHT", "RESPONSIVE", "SUSTAINABLE", "BOLD",
    "PRECISION", "ENGINEERED", "RELENTLESS", "TIMELESS",
  ];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-0 min-h-[100vh] flex flex-col overflow-hidden"
    >
      {/* Background texture */}
      <div className="absolute inset-0 -z-10 grain">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,87,34,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(14,14,16,0.06),transparent_50%)]" />
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 w-full flex-1 grid lg:grid-cols-12 gap-10 items-center pt-6 md:pt-10">
        {/* Left: copy */}
        <div className="lg:col-span-6 z-10 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/5 border border-black/10 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff5722] animate-pulse" />
            <span className="text-xs font-semibold tracking-wider uppercase mono">Drop 07 · Meridian X</span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold text-[13vw] leading-[0.92] md:text-[92px] lg:text-[112px] tracking-[-0.03em]"
          >
            Engineered
            <br />
            <span className="italic font-light">for every</span>
            <br />
            <span className="text-[#ff5722]">step.</span>
          </h1>

          <p className="mt-6 text-base md:text-lg text-black/70 max-w-lg leading-relaxed">
            Precision-crafted sneakers built for the athlete, the artist, and everyone in between. Feel the difference from the first stride.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href="#shop" data-testid="hero-cta-shop" className="btn-primary">
              Shop Now
              <ArrowRight size={16} />
            </a>
            <a href="#collections" data-testid="hero-cta-collection" className="btn-ghost">
              Explore Collection
            </a>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs md:text-sm text-black/60">
            <span className="inline-flex items-center gap-1.5">
              <Truck size={14} /> Free shipping over $75
            </span>
            <span className="hidden md:inline text-black/30">·</span>
            <span className="inline-flex items-center gap-1.5">
              <RotateCcw size={14} /> 30-day returns
            </span>
          </div>
        </div>

        {/* Right: shoe */}
        <div className="lg:col-span-6 relative min-h-[380px] md:min-h-[520px] flex items-center justify-center">
          <div className="hero-blob" aria-hidden="true" />
          <div
            className="relative w-full max-w-[640px] transition-transform duration-500 ease-out"
            ref={shoeRef}
          >
            <div className="float-shoe">
              <img
                src="https://images.unsplash.com/photo-1600185365483-26d7a4cc7519?auto=format&fit=crop&w=1400&q=85"
                alt="Meridian X premium sneaker floating on textured backdrop"
                className="w-full h-auto object-contain drop-shadow-[0_40px_60px_rgba(14,14,16,0.35)]"
                loading="eager"
              />
            </div>
            {/* Callout numbers */}
            <div className="absolute top-6 -left-2 md:left-6 hidden md:flex flex-col items-start">
              <span className="mono text-[10px] text-black/50 tracking-wider">01 · UPPER</span>
              <div className="w-24 h-px bg-black/30 mt-1" />
            </div>
            <div className="absolute bottom-16 -right-2 md:right-6 hidden md:flex flex-col items-end">
              <div className="w-24 h-px bg-black/30 mb-1" />
              <span className="mono text-[10px] text-black/50 tracking-wider">02 · MIDSOLE</span>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="relative border-y border-black/10 bg-[#0e0e10] text-[#f5f3ee] overflow-hidden mt-10">
        <div className="marquee-track flex whitespace-nowrap py-4">
          {[...marqueeWords, ...marqueeWords].map((w, i) => (
            <span
              key={i}
              className="font-display font-bold text-2xl md:text-3xl uppercase px-8 flex items-center gap-8"
            >
              {w}
              <span className="text-[#ff5722] text-3xl leading-none">✦</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
